import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, CalendarDays, MapPin, Loader2, Download, User2, Ticket as TicketIcon, ShieldCheck } from "lucide-react";
import { ticketsApi, ticketPdfUrl, formatAuDate, centsToAUD } from "../lib/events";
import { extractError } from "../lib/api";
import { Logo } from "../components/Logo";

export default function TicketView() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    ticketsApi
      .get(code)
      .then((t) => alive && setTicket(t))
      .catch((e) => alive && setError(extractError(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--gt-bg-marketing)]" data-testid="ticket-loading">
        <Loader2 className="animate-spin text-[var(--gt-ink-2)]" size={20} />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[var(--gt-bg-marketing)]" data-testid="ticket-not-found">
        <div className="max-w-md w-full text-center">
          <div className="gt-label mb-2">Ticket not found</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">
            We couldn&apos;t find that ticket.
          </h1>
          <p className="text-[var(--gt-ink-2)] mb-6">{error || "Please double-check your ticket link."}</p>
          <Link to="/resend" className="gt-btn-primary" data-testid="ticket-goto-resend">
            Resend my tickets
          </Link>
        </div>
      </div>
    );
  }

  const isCheckedIn = ticket.status === "checked_in";
  const isVoid = ["voided", "refunded", "cancelled"].includes(ticket.status);

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="ticket-page">
      <nav className="gt-glass-nav">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="gt-btn-ghost text-sm" data-testid="ticket-home-link">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-14">
        {/* Status banner */}
        {isVoid && (
          <div className="mb-6 border-2 border-[var(--gt-ink)] bg-[var(--gt-accent)] text-white px-4 py-3 text-sm font-semibold tracking-wide" data-testid="ticket-status-void">
            This ticket is {ticket.status.toUpperCase()} and cannot be used for entry.
          </div>
        )}
        {isCheckedIn && (
          <div className="mb-6 border-2 border-[var(--gt-ink)] bg-[var(--gt-ink)] text-white px-4 py-3 text-sm font-semibold tracking-wide flex items-center gap-2" data-testid="ticket-status-checkedin">
            <ShieldCheck size={16} /> Checked in on {formatAuDate(ticket.checked_in_at, ticket.event?.timezone)}
          </div>
        )}

        <div
          className="border-2 border-[var(--gt-ink)] bg-white shadow-[6px_6px_0px_0px_var(--gt-ink)]"
          data-testid="ticket-card"
        >
          {/* Getickets branded banner */}
          <div className="bg-[var(--gt-ink)] text-white px-6 py-5">
            <Logo inverse size="md" />
            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[var(--gt-ink-3)]">
              Australia&apos;s event platform · AUD · GST-Ready
            </div>
          </div>

          {/* Body */}
          <div className="p-6 sm:p-8">
            <div className="gt-label mb-2">Event</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-tight mb-6" data-testid="ticket-event-title">
              {ticket.event?.title}
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3">
                <CalendarDays size={18} className="text-[var(--gt-ink-2)] mt-0.5" />
                <div>
                  <div className="gt-label mb-0.5">Date</div>
                  <div className="text-sm font-semibold" data-testid="ticket-event-date">{formatAuDate(ticket.event?.start_at, ticket.event?.timezone)}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[var(--gt-ink-2)] mt-0.5" />
                <div>
                  <div className="gt-label mb-0.5">Venue</div>
                  <div className="text-sm font-semibold" data-testid="ticket-event-venue">
                    {ticket.event?.venue_name}
                    {ticket.event?.state ? `, ${ticket.event.state}` : ""}
                  </div>
                  {ticket.event?.venue_address && (
                    <div className="text-xs text-[var(--gt-ink-3)] mt-0.5">{ticket.event.venue_address}</div>
                  )}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <TicketIcon size={18} className="text-[var(--gt-ink-2)] mt-0.5" />
                <div>
                  <div className="gt-label mb-0.5">Ticket type</div>
                  <div className="text-sm font-semibold" data-testid="ticket-type-name">{ticket.ticket_type_name}</div>
                  <div className="text-xs text-[var(--gt-ink-3)] mt-0.5">
                    {ticket.price_cents === 0 ? "Complimentary · A$0.00" : centsToAUD(ticket.price_cents)}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User2 size={18} className="text-[var(--gt-ink-2)] mt-0.5" />
                <div>
                  <div className="gt-label mb-0.5">Attendee</div>
                  <div className="text-sm font-semibold" data-testid="ticket-buyer-name">{ticket.buyer_name || "Guest"}</div>
                  <div className="text-xs text-[var(--gt-ink-3)] mt-0.5">{ticket.buyer_email}</div>
                </div>
              </div>
            </div>

            {/* Sprint AK — Token No. panel (display-only, shown when set) */}
            {ticket.token_number && (
              <div
                className="mt-6 border-2 border-[var(--gt-ink)] bg-[var(--gt-bg-surface)] p-4 flex items-center justify-between gap-3"
                data-testid="ticket-token-panel"
              >
                <div>
                  <div className="gt-label mb-0.5" data-testid="ticket-token-label">
                    {ticket.token_label || "Token No."}
                  </div>
                  <div
                    className="font-mono text-2xl font-black tracking-tight text-[var(--gt-ink)]"
                    data-testid="ticket-token-number"
                  >
                    {ticket.token_number}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] max-w-[140px] text-right">
                  Show this at the counter for collection
                </div>
              </div>
            )}

            {/* QR — the main event */}
            <div className="border-t-2 border-dashed border-[var(--gt-ink)] pt-6 mt-6 flex flex-col items-center">
              <div className="p-3 border-2 border-[var(--gt-ink)] bg-white">
                <QRCodeSVG value={ticket.code} size={220} bgColor="#ffffff" fgColor="#09090b" level="M" data-testid="ticket-qr" />
              </div>
              <div className="mt-4 gt-label">Ticket code</div>
              <div className="font-mono text-xl font-bold tracking-wider mt-1" data-testid="ticket-code">{ticket.code}</div>
              <p className="mt-3 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
                Present this QR at the door · Single use
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="border-t-2 border-[var(--gt-ink)] p-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-[var(--gt-ink-3)]">
              Save this page or download the PDF for offline entry.
            </div>
            <a
              href={ticketPdfUrl(ticket.code)}
              target="_blank"
              rel="noreferrer"
              className="gt-btn-primary text-sm"
              data-testid="ticket-download-pdf"
            >
              <Download size={14} /> Download PDF
            </a>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-[var(--gt-ink-3)]">
          Lost your ticket link? <Link to="/resend" className="underline underline-offset-2 text-[var(--gt-ink-2)]" data-testid="ticket-resend-link">Resend to your email</Link>.
        </div>
      </div>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] mt-12">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Logo inverse size="md" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <ManageCookiesLink className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]" />
            <div className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">
              © {new Date().getFullYear()} Getickets
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
